import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { permissionsCover, type AccessView, type DomainSpec, type Kind, type Role } from '@/utils/accessDomains'
import { useUserStore } from '@/stores/user'

// Per org, only the newest request may write the view: a refresh or a role write that
// lands while an older /access request is in flight must not be overwritten by it.
const seq: Record<number, number> = {}
const latest: Record<number, Promise<unknown>> = {}
const pending: Record<number, Promise<unknown>> = {}

const bump = (orgId: number) => (seq[orgId] = (seq[orgId] ?? 0) + 1)

const load = (orgId: number) => {
  const n = bump(orgId)
  const request: Promise<unknown> = API.get(`${HOST}/organization/${orgId}/access`)
    .then(({ data }) => {
      if (seq[orgId] === n) useAccessStore().setView(orgId, data)
      else if (latest[orgId] !== request) return latest[orgId]
    })
    .finally(() => {
      if (pending[orgId] === request) delete pending[orgId]
    })
  latest[orgId] = request
  pending[orgId] = request
  return request
}

const rolesUrl = (orgId: number | null, path = '') => `${HOST}/organization/${orgId}/roles${path}`
const orgUrl = (orgId: number | null, path: string) => `${HOST}/organization/${orgId}${path}`
const isMe = (memberId: number) => useUserStore().member?.member_id === memberId

interface AccessState {
  orgId: number | null
  views: Record<number, AccessView>
}

export const useAccessStore = defineStore('access', {
  state: (): AccessState => ({
    orgId: null,
    views: {},
  }),

  getters: {
    view: (state): AccessView | undefined => (state.orgId == null ? undefined : state.views[state.orgId]),
    loaded(): boolean {
      return !!this.view
    },
    viewFor: state => (orgId: number | string | null | undefined): AccessView | undefined => state.views[Number(orgId)],
    domains() {
      return (kind: Kind): DomainSpec[] => this.view?.domains?.[kind] ?? []
    },
    roles() {
      return (kind?: Kind): Role[] => (this.view?.roles ?? []).filter(r => !kind || r.kind === kind)
    },
    role() {
      return (id: number | string | null | undefined): Role | undefined =>
        (this.view?.roles ?? []).find(r => r.role_id === Number(id))
    },
    myOrgRoleId(): number | null {
      return this.view?.my_org_role_id ?? null
    },
    myProjectRoleId() {
      return (projectId: number | string | null | undefined): number | null | undefined =>
        this.view?.my_project_roles?.[String(projectId)]
    },
    isOwner(): boolean {
      return !!this.view?.is_owner
    },
    isSuperUser(): boolean {
      return !!this.view?.is_super_user
    },
    joinGrants(): number[] {
      return this.view?.my_join_grants ?? []
    },
    ownerMemberId(): number | null {
      return this.view?.owner_member_id ?? null
    },
    defaultJoinRole(): Role | undefined {
      return this.roles('project').find(r => r.is_default_join)
    },
    defaultJoinRoleId(): number | null {
      return this.defaultJoinRole?.role_id ?? null
    },
    defaultJoinRoleName(): string {
      return this.defaultJoinRole?.name ?? 'Viewer'
    },
    roleChoices() {
      return (kind: Kind) => this.roles(kind).map(r => ({ value: r.role_id, label: r.name }))
    },
    // Whether the caller may assign or invite `role`: the Owner and super users may grant anything, anyone else at
    // most their own role's permissions (org role for org roles, their role in `projectId` for project roles).
    grantable() {
      return (role: Role, projectId?: number | string | null): boolean => {
        if (this.isOwner || this.isSuperUser) return true
        const mineId = role.kind === 'project' ? this.myProjectRoleId(projectId) : this.myOrgRoleId
        return permissionsCover(this.role(mineId)?.permissions, role.permissions)
      }
    },
    seededRoleId() {
      return (templateKey: string): number | null =>
        (this.view?.roles ?? []).find(r => r.template_key === templateKey)?.role_id ?? null
    },
  },

  actions: {
    setView(orgId: number, view: AccessView) {
      this.views = { ...this.views, [orgId]: view }
    },

    upsertRole(role: Role) {
      const roles = this.view?.roles
      if (!roles) return
      const index = roles.findIndex(r => r.role_id === role.role_id)
      if (index === -1) roles.push(role)
      else roles.splice(index, 1, role)
    },

    fetch(orgId: number | string) {
      const id = Number(orgId)
      if (!id) return Promise.resolve()
      this.orgId = id
      return load(id)
    },

    ensure(orgId: number | string) {
      const id = Number(orgId)
      if (!id) return Promise.resolve()
      this.orgId = id
      if (this.views[id]) return Promise.resolve()
      return pending[id] ?? load(id)
    },

    refresh(orgId: number | string | null | undefined) {
      const id = Number(orgId)
      return id ? load(id) : Promise.resolve()
    },

    onWsEvent({ event, data }: { event: string, data?: { organization_id?: number | string } }) {
      const orgId = Number(data?.organization_id)
      if (event !== 'roles_changed' || !this.views[orgId]) return
      return this.refresh(orgId).catch(() => {})
    },

    async createRole({ kind, name, description, copyFromRoleId }: { kind: Kind, name: string, description?: string | null, copyFromRoleId?: number | null }) {
      const body: Record<string, unknown> = { kind, name, description }
      if (copyFromRoleId) body.copy_from_role_id = copyFromRoleId
      const { data } = await API.post(rolesUrl(this.orgId), body)
      bump(this.orgId!)
      this.upsertRole(data)
      return data as Role
    },

    async updateRole({ roleId, ...patch }: { roleId: number } & Record<string, unknown>) {
      const { data } = await API.patch(rolesUrl(this.orgId, `/${roleId}`), patch)
      bump(this.orgId!)
      this.upsertRole(data)
      return data as Role
    },

    async resetRole({ roleId, domain }: { roleId: number, domain?: string }) {
      const { data } = await API.post(rolesUrl(this.orgId, `/${roleId}/reset`), null, { params: domain ? { domain } : {} })
      bump(this.orgId!)
      this.upsertRole(data)
      return data as Role
    },

    async deleteRole({ roleId, reassignTo }: { roleId: number, reassignTo?: number | null }) {
      await API.delete(rolesUrl(this.orgId, `/${roleId}`), { params: reassignTo ? { reassign_to: reassignTo } : {} })
      bump(this.orgId!)
      const roles = this.view?.roles
      const index = roles?.findIndex(r => r.role_id === roleId) ?? -1
      if (index !== -1) roles!.splice(index, 1)
      if (reassignTo) await load(this.orgId!)
    },

    async setDefaultJoin(roleId: number) {
      const { data } = await API.put(rolesUrl(this.orgId, `/${roleId}/default-join`))
      bump(this.orgId!)
      this.view?.roles
        ?.filter(r => r.kind === data.kind)
        .forEach(r => { r.is_default_join = r.role_id === data.role_id })
      this.upsertRole(data)
      return data as Role
    },

    async assignOrgRole({ memberId, roleId }: { memberId: number, roleId: number }) {
      await API.put(orgUrl(this.orgId, `/members/${memberId}/role`), { role_id: roleId })
      if (isMe(memberId)) await load(this.orgId!)
    },

    async assignProjectRole({ projectId, memberId, roleId }: { projectId: number, memberId: number, roleId: number }) {
      await API.put(orgUrl(this.orgId, `/project/${projectId}/members/${memberId}/role`), { role_id: roleId })
      if (isMe(memberId)) await load(this.orgId!)
    },

    async transferOwner(memberId: number) {
      await API.post(orgUrl(this.orgId, '/owner/transfer'), { member_id: memberId })
      await load(this.orgId!)
    },
  },
})
